import { api } from './client'
import type { components } from './generated/schema'

export interface ExpensePeriodSum {
    period: string
    total: number
    count: number
}

export interface ExpenseCategorySumRow {
    category_id: string
    category_code: string
    category_name: string
    total: number
    count: number
}

export interface ExpenseVendorSumRow {
    vendor_name: string
    total: number
    count: number
}

export type CashFlowEntryKind = 'income' | 'expense'

export interface CashFlowEntry {
    kind: CashFlowEntryKind
    code: string
    counterparty: string
    amount: number
    payment_method: string
    payment_status: string
    occurred_at: string
    note?: string
}

export interface DailyReportSummary {
    total_income: number
    total_expense: number
    net_cash_flow: number
    income_by_method: Record<string, number>
    expense_by_method: Record<string, number>
    income_count: number
    expense_count: number
}

export interface DailyReportResponse {
    entries: CashFlowEntry[]
    summary: DailyReportSummary
}

export interface ByPeriodParams {
    from: string
    to: string
    period: 'month' | 'quarter' | 'year'
}

export interface DateRangeParams {
    from: string
    to: string
}

export interface DailyReportParams {
    from: string
    to: string
    counterparty?: string
    payment_method?: 'cash' | 'bank_transfer'
    payment_status?: 'paid' | 'unpaid'
    kind?: CashFlowEntryKind
}

/** Mốc thời gian của biểu đồ chi theo danh mục (M-83). */
export type ExpenseBucket = 'day' | 'month' | 'quarter'

export interface ByPeriodCategoryParams extends DateRangeParams {
    bucket: ExpenseBucket
}

export type ExpensePeriodCategoryResponse =
    components['schemas']['ExpensePeriodCategoryResponse']
export type ExpensePeriodCategoryRow =
    ExpensePeriodCategoryResponse['items'][number]

export interface ExpenseReportExpensesParams extends DateRangeParams {
    /** Bỏ trống = mọi danh mục. */
    category_id?: string
}

export type ExpenseReportExpensesResponse =
    components['schemas']['ExpenseReportExpensesResponse']
export type ExpenseReportExpenseRow =
    ExpenseReportExpensesResponse['items'][number]

export const expenseReportApi = {
    byPeriod: (params: ByPeriodParams) =>
        api.get<{ items: ExpensePeriodSum[]; total: number }>(
            '/expense-reports/by-period',
            { params },
        ),
    byCategory: (params: DateRangeParams) =>
        api.get<{ items: ExpenseCategorySumRow[]; total: number }>(
            '/expense-reports/by-category',
            { params },
        ),
    topVendors: (params: DateRangeParams & { limit?: number }) =>
        api.get<{ items: ExpenseVendorSumRow[] }>(
            '/expense-reports/top-vendors',
            { params },
        ),
    byPeriodCategory: (params: ByPeriodCategoryParams) =>
        api.get<ExpensePeriodCategoryResponse>(
            '/expense-reports/by-period-category',
            { params },
        ),
    expenses: (params: ExpenseReportExpensesParams) =>
        api.get<ExpenseReportExpensesResponse>('/expense-reports/expenses', {
            params,
        }),
    daily: (params: DailyReportParams) =>
        api.get<DailyReportResponse>('/expense-reports/daily', { params }),
}
